/**
 * Measurement + annotation data model.
 *
 * Mirrors the DICOM SR Template-2200 structure at a level of detail
 * that matters for clinical workflow — we persist pixel-space
 * coordinates plus enough provenance to reconstruct a valid Basic
 * Text SR or Enhanced SR on submit.
 */

export interface Point2D {
  x: number;  // pixel space, top-left origin
  y: number;
}

export type MeasurementKind =
  | "length"    // 2 points → distance in mm (after PixelSpacing)
  | "angle"     // 3 points → degrees at vertex
  | "cobb"      // 4 points (two lines) → Cobb angle
  | "ellipse"   // 2 points → bounding box of ellipse; HU stats computed
  | "rect"      // 2 points → bounding box
  | "polyline"  // N points → open polyline, length sum
  | "freehand"  // N points → closed polygon
  // Labeling — annotations that carry text rather than a derived
  // numeric value. They share the same persistence path + SR export
  // pipeline so signing a report captures both measurements and
  // labels as Basic Text SR content items.
  | "text"      // 1 point + user-typed string
  | "arrow"     // 2 points: tail → head, with arrowhead at head
  | "pin"       // 1 point + auto-assigned label (A, B, C…)
  // ML-annotation tools — every one carries a classId resolved
  // against labeling/classes.ts at render time so colour + display
  // name can change retroactively.
  | "bbox"      // 2 points = axis-aligned box, with class colour + label
  | "polygon"   // N points = closed polygon (pixel-accurate segmentation)
  | "keypoint"  // 1 point + classLabel (landmark)
  | "mask";     // reference only — actual pixels live in labeling/masks.ts

export interface EllipseStats {
  min: number;
  max: number;
  mean: number;
  stdDev: number;
  areaMm2: number;
  nPixels: number;
}

export interface Measurement {
  id: string;
  kind: MeasurementKind;
  /** SOP Instance UID this measurement belongs to. */
  instanceUid: string;
  /** Frame (for multi-frame SOPs) — 1-based per DICOM convention. */
  frameNumber: number;
  points: Point2D[];
  /** Free-text label the clinician types in. */
  label?: string;
  /** Derived value (mm / degrees / mm²) for display; recomputed on
   *  re-render so the source of truth is always `points`. */
  derivedMm?: number;
  /** Row/col pixel spacing in mm, captured from the SOP at creation
   *  time. Stored so the label can show W × H in mm post-hoc even
   *  after the instance has been swapped out of the cache. */
  pixelSpacingMm?: [number, number];
  /** For ellipse ROI — computed from the decoded pixel buffer. */
  stats?: EllipseStats;
  /** For text / arrow — user-typed free-text label. */
  text?: string;
  /** For pin — auto-assigned short label (A, B, C, …, Z, AA, AB, …). */
  pinLabel?: string;
  /** ML-labeling tools carry a class reference — the colour +
   *  display name are resolved at render time against the class
   *  registry so renaming a class updates existing annotations. */
  classId?: string;
  createdAtUnix: number;
}

/** Produce the next pin label (A..Z, AA..AZ, BA..) based on how many
 *  pins already exist on the same instance. */
export function nextPinLabel(
  measurements: Measurement[],
  instanceUid: string,
): string {
  const n = measurements.filter((m) => m.kind === "pin" && m.instanceUid === instanceUid).length;
  let s = "";
  let x = n;
  do {
    s = String.fromCharCode(65 + (x % 26)) + s;
    x = Math.floor(x / 26) - 1;
  } while (x >= 0);
  return s;
}

export function newMeasurement(kind: MeasurementKind, instanceUid: string, frameNumber = 1): Measurement {
  return {
    id: crypto.randomUUID(),
    kind,
    instanceUid,
    frameNumber,
    points: [],
    createdAtUnix: Math.floor(Date.now() / 1000),
  };
}

/** Distance in pixels between two points. */
export function pxDist(a: Point2D, b: Point2D): number {
  const dx = a.x - b.x, dy = a.y - b.y;
  return Math.hypot(dx, dy);
}

/** Total polyline length in pixels. */
export function polyPxLength(points: Point2D[]): number {
  let s = 0;
  for (let i = 1; i < points.length; i++) s += pxDist(points[i - 1], points[i]);
  return s;
}

/** Angle in degrees at b (vertex), with a + c as the two arms. */
export function angleDeg(a: Point2D, b: Point2D, c: Point2D): number {
  const v1x = a.x - b.x, v1y = a.y - b.y;
  const v2x = c.x - b.x, v2y = c.y - b.y;
  const dot = v1x * v2x + v1y * v2y;
  const m1 = Math.hypot(v1x, v1y);
  const m2 = Math.hypot(v2x, v2y);
  if (m1 === 0 || m2 === 0) return 0;
  const cos = Math.min(1, Math.max(-1, dot / (m1 * m2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

/** Cobb angle — 4 points define 2 lines; answer is the acute angle
 *  between them (0–90°). Used in scoliosis measurement. */
export function cobbDeg(p1: Point2D, p2: Point2D, p3: Point2D, p4: Point2D): number {
  const v1x = p2.x - p1.x, v1y = p2.y - p1.y;
  const v2x = p4.x - p3.x, v2y = p4.y - p3.y;
  const m1 = Math.hypot(v1x, v1y), m2 = Math.hypot(v2x, v2y);
  if (m1 === 0 || m2 === 0) return 0;
  const cos = Math.abs((v1x * v2x + v1y * v2y) / (m1 * m2));
  return (Math.acos(Math.min(1, cos)) * 180) / Math.PI;
}

/** HU stats over an ellipse bounding-box (a,c) against the decoded
 *  single-channel 16-bit stored pixels + rescale slope/intercept. */
export function ellipseStats(
  a: Point2D, c: Point2D,
  stored: Uint16Array | Int16Array,
  width: number, height: number,
  rescaleSlope: number, rescaleIntercept: number,
  pixelSpacingMmX: number, pixelSpacingMmY: number,
): EllipseStats {
  const x0 = Math.min(a.x, c.x), x1 = Math.max(a.x, c.x);
  const y0 = Math.min(a.y, c.y), y1 = Math.max(a.y, c.y);
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
  const rx = (x1 - x0) / 2, ry = (y1 - y0) / 2;
  if (rx <= 0 || ry <= 0) return { min: 0, max: 0, mean: 0, stdDev: 0, areaMm2: 0, nPixels: 0 };
  let n = 0, sum = 0, sumSq = 0, mn = +Infinity, mx = -Infinity;
  const xL = Math.max(0, Math.floor(x0)), xH = Math.min(width - 1,  Math.ceil(x1));
  const yL = Math.max(0, Math.floor(y0)), yH = Math.min(height - 1, Math.ceil(y1));
  for (let y = yL; y <= yH; y++) {
    const dy = (y - cy) / ry;
    for (let x = xL; x <= xH; x++) {
      const dx = (x - cx) / rx;
      if (dx * dx + dy * dy > 1) continue;
      const hu = stored[y * width + x] * rescaleSlope + rescaleIntercept;
      if (hu < mn) mn = hu;
      if (hu > mx) mx = hu;
      sum += hu; sumSq += hu * hu; n++;
    }
  }
  if (!n) return { min: 0, max: 0, mean: 0, stdDev: 0, areaMm2: 0, nPixels: 0 };
  const mean = sum / n;
  const variance = Math.max(0, sumSq / n - mean * mean);
  const areaMm2 = Math.PI * rx * ry * pixelSpacingMmX * pixelSpacingMmY;
  return { min: mn, max: mx, mean, stdDev: Math.sqrt(variance), areaMm2, nPixels: n };
}
